/**
 * recentJobsStore.ts – lightweight localStorage log of the last N print/export
 * jobs (max 10). Pure-front office history so a user can re-load yesterday's
 * config without digging through uploads again. No PII beyond file basenames.
 */

const STORAGE_KEY = "tvs-excel-uploader:recent-jobs";
const MAX_JOBS = 10;

/** Broadcast event fired when the recent-jobs list changes, so the panel
 *  refreshes live in the SAME tab (localStorage 'storage' only fires in other
 *  tabs). */
export const RECENT_JOBS_EVENT = "excel-uploader:recent-jobs-changed";

/** Dispatch a same-tab refresh event (no-op when events are unavailable). */
function broadcastChange(): void {
  try {
    window.dispatchEvent(new Event(RECENT_JOBS_EVENT));
  } catch {
    // ignore
  }
}

export type JobKind = "pdf-single" | "pdf-all" | "pdf-zip" | "print";

export interface RecentJob {
  /** Stable id = timestamp + kind. */
  id: string;
  /** When the job was started (epoch ms). */
  startedAt: number;
  /** Type of export. */
  kind: JobKind;
  /** Excel file basename (no path) — user-facing, helps recall. */
  excelFile: string;
  /** Template file basename. */
  templateFile: string;
  /** Sheet name used. */
  sheetName: string;
  /** Number of pages produced. */
  pageCount: number;
  /** Progress 0-100 (100 = done). Optional for completed jobs. */
  progress?: number;
  /** Optional grouping-column label. */
  groupLabel?: string;
  /** Optional paper override label (e.g. "A4 ngang"). */
  paper?: string;
  /** Status flag. */
  status: "ok" | "error" | "partial";
  /** Short note, e.g. error message or "2 trang overflow". */
  note?: string;
}

/** Read-only accessor (safe if localStorage is unavailable / corrupted). */
export function listRecentJobs(): RecentJob[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentJob).slice(0, MAX_JOBS);
  } catch {
    return [];
  }
}

/** Append a new job to the front of the list (dedup by id). */
export function recordRecentJob(job: Omit<RecentJob, "id" | "startedAt"> & Partial<Pick<RecentJob, "id" | "startedAt">>): RecentJob {
  const full: RecentJob = {
    id: job.id ?? `${Date.now().toString(36)}-${job.kind}-${Math.random().toString(36).slice(2, 8)}`,
    startedAt: job.startedAt ?? Date.now(),
    ...job,
  } as RecentJob;
  if (typeof localStorage === "undefined") return full;
  try {
    const next = [full, ...listRecentJobs().filter((j) => j.id !== full.id)].slice(0, MAX_JOBS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    broadcastChange();
  } catch {
    // localStorage quota or disabled — swallow.
  }
  return full;
}

/** Clear the whole recent-jobs log. */
export function clearRecentJobs(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    broadcastChange();
  } catch {
    // ignore
  }
}

/** Remove a single job by id. */
export function removeRecentJob(id: string): void {
  if (typeof localStorage === "undefined") return;
  try {
    const next = listRecentJobs().filter((j) => j.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    broadcastChange();
  } catch {
    // ignore
  }
}

/**
 * Update the progress (and optionally status/note) of an in-flight job, so the
 * Lịch sử in gần đây panel can show a live % while an export runs.
 */
export function updateRecentJobProgress(
  id: string,
  progress: number,
  patch?: Partial<Pick<RecentJob, "status" | "note">>
): void {
  if (typeof localStorage === "undefined") return;
  try {
    const next = listRecentJobs().map((j) =>
      j.id === id ? { ...j, progress, ...patch } : j
    );
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    broadcastChange();
  } catch {
    // ignore
  }
}

function isRecentJob(v: unknown): v is RecentJob {
  if (!v || typeof v !== "object") return false;
  const j = v as Record<string, unknown>;
  return (
    typeof j.id === "string" &&
    typeof j.startedAt === "number" &&
    typeof j.kind === "string" &&
    typeof j.excelFile === "string" &&
    typeof j.templateFile === "string" &&
    typeof j.pageCount === "number"
  );
}
